import React, { useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import appVersion from "config/appVersion";
import { APP_VERSION } from "config/env";
import changelog from "config/changelog.gen";
import SandboxBadge from "components/common/SandboxBadge";

// The clickable version caption (authenticated UI only) + "What's new" dialog.
// A badge dot marks versions this browser hasn't seen yet; opening the dialog
// marks the current version seen.
const LAST_SEEN_KEY = "drcfo_admin_last_seen_version";

const WhatsNew = () => {
  const [open, setOpen] = useState(false);
  const [lastSeen, setLastSeen] = useState(
    () => localStorage.getItem(LAST_SEEN_KEY) || ""
  );

  const currentVersion = APP_VERSION || "";
  if (!appVersion) return null;

  // What follows the release number (SHA, environment) only a non-prod build adds.
  const [release, ...build] = appVersion.split(" · ");
  const hasNews = changelog.length > 0;
  const unseen = hasNews && !!currentVersion && lastSeen !== currentVersion;

  const handleOpen = () => {
    setOpen(true);
    if (currentVersion) {
      localStorage.setItem(LAST_SEEN_KEY, currentVersion);
      setLastSeen(currentVersion);
    }
  };

  return (
    <>
      <Badge
        color="primary"
        variant="dot"
        invisible={!unseen}
        className="app_version"
      >
        <button
          type="button"
          className="app_version_button"
          onClick={handleOpen}
          title="What's new"
        >
          {release}
        </button>
        {build.length > 0 && (
          <SandboxBadge sx={{ ml: 0.5 }}>
            <span className="app_version_build">{build.join(" · ")}</span>
          </SandboxBadge>
        )}
      </Badge>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>What's new</DialogTitle>
        <DialogContent dividers>
          {changelog.map((release) => (
            <div key={release.version} className="whats_new_release">
              <Typography variant="subtitle1" component="h3">
                v{release.version}
                {release.date && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    {release.date}
                  </Typography>
                )}
              </Typography>
              {release.sections.map((section) => (
                <div key={section.title}>
                  <Typography variant="subtitle2" sx={{ mt: 1 }}>
                    {section.title}
                  </Typography>
                  <ul className="whats_new_items">
                    {section.items.map((item, i) => (
                      <li key={`${section.title}-${i}`}>
                        <Typography variant="body2">{item}</Typography>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ))}
          {!hasNews && (
            <Typography variant="body2">
              No release notes available for this build.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WhatsNew;
